import React, { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getFavoriteEmojis, bumpFavoriteEmoji } from "../../../../utils/emojiFavorites";

import "./index.scss";
import "../../../molecules/Composer/composer-v2.scss"; // reuse existing rich-text styles
import ScrollArea from "../../../atoms/ScrollArea";
import MessageRow from "../../../molecules/MessageRow";
import MessageRowCompact from "../../../molecules/MessageRowCompact";
import ChatBubble from "../../../molecules/ChatBubble";
import MessageActionsPanel from "../../../molecules/MessageActionsPanel";
import MessageReactions from "../../../molecules/MessageReactions";
import MessageThreadBar from "../../../molecules/MessageThreadBar";
import DateSeparator from "../../../molecules/DateSeparator";
import StickyDateHeader from "../../../molecules/StickyDateHeader";
import ChatStickyOverlay from "../../../molecules/ChatStickyOverlay";
import ChatTypingIndicator from "../../../molecules/ChatTypingIndicator";
import VirtualizedItem from "../../../molecules/VirtualizedItem";
import DropdownMenuFloat from "../../../molecules/DropdownMenuFloat";
import DeleteMessagePopup from "../../../molecules/DeleteMessagePopup";
import PopupOverlay from "../../Background/PopupOverlay";
import chatService from "../../../../services/chatService";
import useImageLightbox from "../../../../hooks/useImageLightbox";
import useLocaleDateFormatter from "../../../../hooks/useLocaleDateFormatter";
import { attachmentsToItems } from "../../../../utils/imageUtils";
import { getDateSeparatorMetadata } from "../../../../utils/dateLabel";
import LoadingSpinner from "../../../atoms/LoadingSpinner";
import MessageInlineEditor from "../../../molecules/MessageInlineEditor";
import { ReactComponent as ReplyIcon } from "../../../../images/reply-icon.svg";
import { ReactComponent as PencilIcon } from "../../../../images/pencil.svg";
import { ReactComponent as TrashIcon } from "../../../../images/trash.svg";
import MessageCommentDrawer from "./MessageCommentDrawer";
import RichTextRenderer from "../../../molecules/Composer/RichTextRenderer";
import ChatImage from "../../../molecules/ChatImage";
import ContentWithLinkPreviews from "../../../molecules/ContentWithLinkPreviews";
import { extractSupportedPreviewUrlsFromHtml } from "../../../../utils/linkDetection";
import { resolveUserDisplayName } from "../../../../utils/userIdentity";
import { formatMessagePreview } from "../../../../utils/messageUtils";
import { resolveChatMessageAuthorName } from "../../../../utils/chatMessageAuthor";

// local time formatting handled inside MessageRow/ChatBubble helpers

const DEFAULT_SEPARATOR_HEIGHT = 38;
const DEFAULT_MESSAGE_HEIGHT = 148;
const DEFAULT_TYPING_HEIGHT = 72;
const LOADER_HEIGHT = 56;
const STICKY_DATE_TOP_PX = 8;

const isOlderMessagesLoadSuccessful = (result) => (
    result === true || (result && typeof result === "object" && result.ok === true)
);

const decodeHtmlAttributeValue = (value) => {
    if (value === undefined || value === null) {
        return "";
    }
    const raw = String(value);
    return raw
        .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
            const codePoint = Number.parseInt(hex, 16);
            return Number.isFinite(codePoint) && codePoint >= 0 && codePoint <= 0x10FFFF
                ? String.fromCodePoint(codePoint)
                : _;
        })
        .replace(/&#([0-9]+);/g, (_, decimal) => {
            const codePoint = Number.parseInt(decimal, 10);
            return Number.isFinite(codePoint) && codePoint >= 0 && codePoint <= 0x10FFFF
                ? String.fromCodePoint(codePoint)
                : _;
        })
        .replace(/&#0*61;|&#x0*3d;|&equals;/gi, "=")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, "\"")
        .replace(/&#0*39;|&#x0*27;/gi, "'");
};

const normalizeInlineImageUrl = (value) => decodeHtmlAttributeValue(value).trim();

const extractInlineImageUrlsFromHtml = (html) => {
    const urls = new Set();
    if (!html || typeof html !== "string" || !/<\s*img\b/i.test(html)) {
        return urls;
    }

    if (typeof DOMParser !== "undefined") {
        try {
            const doc = new DOMParser().parseFromString(html, "text/html");
            doc.querySelectorAll("img[src]").forEach((img) => {
                const src = normalizeInlineImageUrl(img.getAttribute("src"));
                if (src) {
                    urls.add(src);
                }
            });
            return urls;
        } catch (_) {
            // Fall through to regex extraction.
        }
    }

    const pattern = /<img\b[^>]*\bsrc\s*=\s*(["'])(.*?)\1/gi;
    let match = pattern.exec(html);
    while (match) {
        const src = normalizeInlineImageUrl(match[2]);
        if (src) {
            urls.add(src);
        }
        match = pattern.exec(html);
    }
    return urls;
};

const getMessageReplyPreview = (message) => {
    const reply = message?.replyTo && typeof message.replyTo === "object" ? message.replyTo : null;
    const messageId = reply?.messageId ?? message?.replyToMessageId;
    if (messageId === undefined || messageId === null || !String(messageId).trim()) {
        return null;
    }

    return {
        messageId: String(messageId).trim(),
        senderId: reply?.senderId === undefined || reply?.senderId === null ? null : String(reply.senderId),
        content: typeof reply?.content === "string" ? reply.content : "",
    };
};

function MessageReplyQuote({
    reply,
    currentUserId,
    currentUser,
    resolveUserName,
    fallbackAuthor,
    fallbackPreview,
    onOpen,
    ariaLabel,
}) {
    if (!reply) {
        return null;
    }

    const author = resolveChatMessageAuthorName({
        message: { senderId: reply.senderId },
        resolveUserName,
        currentUserId,
        currentUser,
        fallback: fallbackAuthor,
    });
    const preview = formatMessagePreview(reply.content, {
        fallback: fallbackPreview,
        maxLength: 120,
    });

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen?.(reply.messageId);
    };

    return (
        <button
            type="button"
            className="chat-message-reply"
            data-reply-to-message-id={reply.messageId}
            onClick={handleClick}
            aria-label={ariaLabel}
        >
            <div className="chat-message-reply__author">{author}</div>
            <div className="chat-message-reply__content">{preview}</div>
        </button>
    );
}

function ChatBubbleRow({
    message,
    messageId,
    currentUserId,
    content,
    activeMessageId,
    setActiveMessageId,
    quickEmojis,
    onQuickReact,
    onEmojiPickerOpenChange,
    onMore,
    reactions,
    onToggleReaction,
    canReact,
    threadReplyCount,
    onOpenThread,
    onRightClick,
    isEditing,
    formatLocalTime,
}) {
    const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
    const isSelf = currentUserId != null && String(message.senderId).trim() === String(currentUserId).trim();
    const rowClassName = `chat-bubble-row${isSelf ? " chat-bubble-row--self" : ""}`;
    const showActions = !isEditing && (isEmojiPickerOpen || activeMessageId === messageId);

    return (
        <div
            className={rowClassName}
            role="listitem"
            tabIndex={0}
            onMouseEnter={() => setActiveMessageId?.(messageId)}
            onFocus={() => setActiveMessageId?.(messageId)}
            onMouseLeave={(event) => {
                if (isEmojiPickerOpen) return;
                // On touch, browsers emit a mouseleave right after the tap's
                // compat mouse events; the row keeps focus, so let blur own
                // the dismissal in that case.
                const row = event.currentTarget;
                if (row && typeof document !== "undefined" && row.contains(document.activeElement)) return;
                setActiveMessageId?.(null);
            }}
            onBlur={(event) => {
                if (isEmojiPickerOpen) return;
                // Ignore focus moving within the row (e.g. onto an action
                // button) — only clear when focus truly leaves it.
                const next = event.relatedTarget;
                if (next && event.currentTarget.contains(next)) return;
                setActiveMessageId?.(null);
            }}
            onContextMenu={onRightClick}
        >
            <ChatBubble
                msg={message}
                currentUserId={currentUserId}
                formatLocalTime={formatLocalTime}
                onRightClick={onRightClick}
                showDropdown={false}
                contentRenderer={() => content}
            />

            {!isEditing && Number.isFinite(threadReplyCount) && threadReplyCount > 0 && (
                <MessageThreadBar
                    count={threadReplyCount}
                    onOpen={onOpenThread}
                />
            )}

            {!isEditing && Array.isArray(reactions) && reactions.length > 0 && (
                <MessageReactions
                    reactions={reactions}
                    onToggleReaction={onToggleReaction}
                    canReact={canReact}
                />
            )}

            {showActions && (
                <MessageActionsPanel
                    quickEmojis={quickEmojis}
                    onQuickReact={onQuickReact}
                    onEmojiPickerOpenChange={(open) => {
                        setIsEmojiPickerOpen(Boolean(open));
                        onEmojiPickerOpenChange?.(open);
                        if (open) {
                            setActiveMessageId?.(messageId);
                        }
                    }}
                    onMore={onMore}
                />
            )}
        </div>
    );
}

const ChatMessages = forwardRef(function ChatMessages({
    messages,
    setMessages,
    currentUserId,
    onDeleteMessage,
    onEditMessage,
    onDeleteForMe,
    onReply,
    typingUserIds,
    resolveUserName = () => "",
    resolveMessageStatus,
    contentRenderer,
    useBubbleLayout = false,
    onReact,
    onComment,
    onMore,
    commentPanelState,
    onCommentPanelStateChange,
    quickEmojis,
    canReact = true,
    stompClient,
    isSocketConnected,
    hasMoreAbove = false,
    isFetchingOlder = false,
    onLoadOlderMessages,
    isInitialLoading = false,
    conversationKey,
}, ref) {
    const viewportRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const containerRef = useRef(null);
    const loadMoreLockRef = useRef(false);
    const prevTailMetaRef = useRef({ tailId: null, length: 0 });
    const prevTypingKeyRef = useRef("");
    const settleScrollFrameRef = useRef(null);
    const highlightTimeoutsRef = useRef(new Map());
    const replyQuoteNavigationRequestRef = useRef(0);
    const isMountedRef = useRef(true);
    const [activeMessageId, setActiveMessageId] = useState(null);
    const [menuState, setMenuState] = useState({ open: false, messageId: null, x: 0, y: 0 });
    const [deleteMessageTarget, setDeleteMessageTarget] = useState(null);
    const [commentState, setCommentState] = useState({ open: false, message: null });
    const commentMessageId = commentState.message ? String(commentState.message.id) : null;
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [replyQuoteNavigation, setReplyQuoteNavigation] = useState(null);
    const { lightboxEl, openWithItems, getRichContentClickHandler } = useImageLightbox();
    const [commentsWidth, setCommentsWidth] = useState(() => {
        try {
            const stored = Number(window.localStorage.getItem("chat.commentsWidth"));
            if (Number.isFinite(stored)) {
                return Math.max(320, Math.min(stored, 720));
            }
        } catch (_) {}
        return 380;
    });
    const [isResizing, setIsResizing] = useState(false);
    // Local draft/sending state managed inside MessageCommentDrawer
    const favoriteQuickEmojis = useMemo(() => (quickEmojis && quickEmojis.length ? quickEmojis : getFavoriteEmojis()), [quickEmojis]);
    const authUser = useSelector((state) => state.auth && state.auth.user);
    const { formatDate, formatTime } = useLocaleDateFormatter();
    const resolvedConversationKey = useMemo(() => {
        if (conversationKey !== undefined && conversationKey !== null) {
            return String(conversationKey);
        }
        const firstChatId = Array.isArray(messages)
            ? messages.find((message) => message?.chatId !== undefined && message?.chatId !== null)?.chatId
            : null;
        return firstChatId !== undefined && firstChatId !== null ? String(firstChatId) : "";
    }, [conversationKey, messages]);
    const conversationKeyRef = useRef(resolvedConversationKey);
    conversationKeyRef.current = resolvedConversationKey;
    const currentUserIdString = useMemo(
        () => (currentUserId === undefined || currentUserId === null ? null : String(currentUserId)),
        [currentUserId]
    );
    const { t } = useTranslation();

    useEffect(() => {
        replyQuoteNavigationRequestRef.current += 1;
        setReplyQuoteNavigation(null);
        setDeleteMessageTarget(null);
    }, [resolvedConversationKey]);

    const formatMessageTime = useCallback((timestamp) => {
        if (!timestamp) {
            return "";
        }
        const parsed = new Date(timestamp);
        if (Number.isNaN(parsed.getTime())) {
            return "";
        }
        return formatTime(parsed);
    }, [formatTime]);

    const notifyCommentStateChange = useCallback((open, message) => {
        if (typeof onCommentPanelStateChange !== "function") {
            return;
        }
        onCommentPanelStateChange({
            open,
            messageId: message?.id ?? null,
            message: message ?? null,
        });
    }, [onCommentPanelStateChange]);

    const selfName = useMemo(() => {
        return resolveUserDisplayName(authUser, "");
    }, [authUser]);
    const selfAvatar = useMemo(() => authUser?.avatarUrl || authUser?.profilePicture || null, [authUser?.avatarUrl, authUser?.profilePicture]);

    const performScrollToBottom = useCallback((options = {}) => {
        const viewport = viewportRef.current;
        const anchor = bottomAnchorRef.current;
        const behavior = options?.behavior ?? "smooth";

        if (anchor && typeof anchor.scrollIntoView === "function") {
            anchor.scrollIntoView({ block: "end", inline: "nearest", behavior });
            return;
        }

        if (!viewport) {
            return;
        }

        if (typeof viewport.scrollTo === "function") {
            viewport.scrollTo({ top: viewport.scrollHeight, behavior });
        } else {
            viewport.scrollTop = viewport.scrollHeight;
        }
    }, []);

    const scrollToMessage = useCallback((messageId, options = {}) => {
        if (!messageId) {
            return false;
        }
        const viewport = viewportRef.current;
        if (!viewport) {
            return false;
        }
        const id = String(messageId);
        const escapedId = typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(id)
            : id.replace(/"/g, '\\"');
        const selector = `[data-message-id="${escapedId}"]`;
        const messageSelector = `.chat-messages__item${selector}`;
        const findRenderedMessage = () => viewport.querySelector(messageSelector);
        const findAnyTarget = () => findRenderedMessage() || viewport.querySelector(selector);
        const target = findAnyTarget();
        if (!target) {
            return false;
        }

        target.scrollIntoView({
            block: "center",
            inline: "nearest",
            behavior: options.behavior || "smooth",
        });

        const highlight = () => {
            const messageNode = findRenderedMessage();
            if (!messageNode) {
                return false;
            }
            const existingTimeout = highlightTimeoutsRef.current.get(id);
            if (existingTimeout) {
                window.clearTimeout(existingTimeout);
            }
            messageNode.classList.remove("chat-messages__item--highlight");
            // Force a reflow so repeated clicks replay the one-shot animation.
            void messageNode.offsetWidth;
            messageNode.classList.add("chat-messages__item--highlight");
            const timeoutId = window.setTimeout(() => {
                messageNode.classList.remove("chat-messages__item--highlight");
                highlightTimeoutsRef.current.delete(id);
            }, 1800);
            highlightTimeoutsRef.current.set(id, timeoutId);
            return true;
        };

        if (typeof window === "undefined") {
            return true;
        }

        window.setTimeout(() => {
            if (!highlight()) {
                let attempts = 0;
                const maxAttempts = 20;
                const retryId = window.setInterval(() => {
                    attempts += 1;
                    const success = highlight();
                    if (success || attempts >= maxAttempts) {
                        window.clearInterval(retryId);
                    }
                }, 80);
            }
        }, options.highlightDelayMs ?? 240);

        return true;
    }, []);

    useImperativeHandle(ref, () => ({
        preserveScroll(updater) {
            if (typeof updater !== "function") {
                return;
            }
            const viewport = viewportRef.current;
            if (!viewport) {
                updater();
                return;
            }
            const previousScrollHeight = viewport.scrollHeight;
            const previousScrollTop = viewport.scrollTop;
            updater();
            if (typeof window === "undefined") {
                return;
            }
            window.requestAnimationFrame(() => {
                const node = viewportRef.current;
                if (!node) {
                    return;
                }
                const delta = node.scrollHeight - previousScrollHeight;
                if (delta !== 0) {
                    node.scrollTop = previousScrollTop + delta;
                }
            });
        },
        scrollToBottom(options = { behavior: "smooth" }) {
            performScrollToBottom(options);

            if (!options?.settle || typeof window === "undefined") {
                return;
            }

            if (settleScrollFrameRef.current !== null) {
                window.cancelAnimationFrame(settleScrollFrameRef.current);
            }

            settleScrollFrameRef.current = window.requestAnimationFrame(() => {
                settleScrollFrameRef.current = null;
                performScrollToBottom({ ...options, behavior: "auto" });
            });
        },
        getViewport: () => viewportRef.current,
        scrollToMessage,
    }), [performScrollToBottom, scrollToMessage]);

    const closeMenu = useCallback(() => setMenuState((s) => ({ ...s, open: false })), []);
    const closeDeleteMessagePopup = useCallback(() => setDeleteMessageTarget(null), []);

    const buildContextMenuItems = useCallback((target) => {
        if (!target) {
            return [];
        }
        const isOwner = currentUserIdString && String(target.senderId) === currentUserIdString;
        const items = [];

        if (typeof onReply === "function") {
            items.push({
                icon: <ReplyIcon />,
                label: t("actions.reply"),
                onClick: () => {
                    closeMenu();
                    onReply?.(target);
                },
            });
        }

        if (isOwner) {
            items.push({
                icon: <PencilIcon />,
                label: t("actions.edit"),
                onClick: () => {
                    closeMenu();
                    setEditingMessageId(target.id);
                },
            });
            items.push({
                icon: <TrashIcon />,
                label: t("actions.delete"),
                tone: "danger",
                onClick: () => {
                    closeMenu();
                    setDeleteMessageTarget(target);
                },
            });
        } else if (typeof onDeleteForMe === "function") {
            items.push({
                icon: <TrashIcon />,
                label: t("actions.delete"),
                tone: "danger",
                onClick: () => {
                    closeMenu();
                    setDeleteMessageTarget(target);
                },
            });
        }

        return items;
    }, [closeMenu, currentUserIdString, onDeleteForMe, onReply, setEditingMessageId, t]);

    const handleRightClick = useCallback((event, messageId) => {
        if (!event) {
            return;
        }
        const target = messages.find((m) => m && String(m.id) === String(messageId));
        const items = buildContextMenuItems(target);
        if (!items.length) {
            closeMenu();
            return;
        }
        event.preventDefault();
        setMenuState({ open: true, messageId, x: event.clientX, y: event.clientY });
    }, [buildContextMenuItems, closeMenu, messages]);

    const handleMoreClick = useCallback((msg, event) => {
        const items = buildContextMenuItems(msg);
        if (!items.length) {
            closeMenu();
            return;
        }
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const rect = event?.currentTarget?.getBoundingClientRect?.();
        const nextX = rect ? rect.right : (event?.clientX || 0);
        const nextY = rect ? rect.bottom : (event?.clientY || 0);
        setMenuState((prev) => {
            const isSame = prev.open && String(prev.messageId) === String(msg?.id);
            if (isSame) {
                return { open: false, messageId: null, x: nextX, y: nextY };
            }
            return { open: true, messageId: msg?.id, x: nextX, y: nextY };
        });
    }, [buildContextMenuItems, closeMenu]);

    const requestOlderMessages = useCallback(() => {
        if (!hasMoreAbove || typeof onLoadOlderMessages !== "function") {
            return null;
        }
        if (loadMoreLockRef.current || isFetchingOlder) {
            return null;
        }
        loadMoreLockRef.current = true;
        try {
            const maybePromise = onLoadOlderMessages();
            if (maybePromise && typeof maybePromise.then === "function") {
                return Promise.resolve(maybePromise).finally(() => {
                    loadMoreLockRef.current = false;
                });
            }
            loadMoreLockRef.current = false;
            return maybePromise;
        } catch (error) {
            loadMoreLockRef.current = false;
            throw error;
        }
    }, [hasMoreAbove, onLoadOlderMessages, isFetchingOlder]);

    const invokeLoadOlder = useCallback(() => {
        const maybePromise = requestOlderMessages();
        if (maybePromise && typeof maybePromise.catch === "function") {
            maybePromise.catch(() => {
                // The loader owner handles user-facing failures; keep scroll listeners quiet.
            });
        }
    }, [requestOlderMessages]);

    const handleScroll = useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const threshold = 72;
        if (viewport.scrollTop <= threshold) {
            invokeLoadOlder();
        }
    }, [invokeLoadOlder]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }
        viewport.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            viewport.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    useEffect(() => {
        if (!isFetchingOlder) {
            loadMoreLockRef.current = false;
        }
    }, [isFetchingOlder]);

    useEffect(() => {
        if (!hasMoreAbove) {
            loadMoreLockRef.current = false;
        }
    }, [hasMoreAbove]);

    useEffect(() => () => {
        isMountedRef.current = false;
        if (typeof window === "undefined") {
            return;
        }
        if (settleScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(settleScrollFrameRef.current);
            settleScrollFrameRef.current = null;
        }
        highlightTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        highlightTimeoutsRef.current.clear();
    }, []);

    const handleOpenReplyQuote = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        if (scrollToMessage(messageId)) {
            setReplyQuoteNavigation(null);
            return;
        }
        const requestId = replyQuoteNavigationRequestRef.current + 1;
        replyQuoteNavigationRequestRef.current = requestId;
        setReplyQuoteNavigation({
            messageId: String(messageId),
            requestId,
            conversationKey: resolvedConversationKey,
            pagesRequested: 0,
            isLoading: false,
        });
    }, [resolvedConversationKey, scrollToMessage]);

    useEffect(() => {
        if (!replyQuoteNavigation) {
            return undefined;
        }

        const {
            messageId,
            requestId,
            conversationKey: navigationConversationKey,
            pagesRequested,
            isLoading,
        } = replyQuoteNavigation;

        if (navigationConversationKey !== conversationKeyRef.current) {
            setReplyQuoteNavigation(null);
            return undefined;
        }

        const scrollOptions = pagesRequested > 0
            ? { behavior: "auto", highlightDelayMs: 0 }
            : undefined;

        if (scrollToMessage(messageId, scrollOptions)) {
            setReplyQuoteNavigation(null);
            return undefined;
        }

        if (isLoading || isFetchingOlder || loadMoreLockRef.current) {
            return undefined;
        }

        if (!hasMoreAbove) {
            setReplyQuoteNavigation(null);
            return undefined;
        }

        const maybePromise = requestOlderMessages();
        if (!maybePromise || typeof maybePromise.then !== "function") {
            setReplyQuoteNavigation(null);
            return undefined;
        }

        setReplyQuoteNavigation((current) => {
            if (!current || current.requestId !== requestId) {
                return current;
            }
            return {
                ...current,
                pagesRequested: current.pagesRequested + 1,
                isLoading: true,
            };
        });

        Promise.resolve(maybePromise)
            .then((loadResult) => {
                if (!isOlderMessagesLoadSuccessful(loadResult)) {
                    setReplyQuoteNavigation((current) => (
                        current && current.requestId === requestId ? null : current
                    ));
                    return;
                }

                const resumeNavigation = () => {
                    if (!isMountedRef.current) {
                        return;
                    }
                    if (conversationKeyRef.current !== navigationConversationKey) {
                        return;
                    }
                    setReplyQuoteNavigation((current) => {
                        if (!current || current.requestId !== requestId) {
                            return current;
                        }
                        return {
                            ...current,
                            isLoading: false,
                        };
                    });
                };

                if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
                    window.requestAnimationFrame(resumeNavigation);
                } else {
                    resumeNavigation();
                }
            })
            .catch(() => {
                if (!isMountedRef.current) {
                    return;
                }
                if (conversationKeyRef.current !== navigationConversationKey) {
                    return;
                }
                setReplyQuoteNavigation((current) => (
                    current && current.requestId === requestId ? null : current
                ));
            });

        return undefined;
    }, [
        hasMoreAbove,
        isFetchingOlder,
        replyQuoteNavigation,
        requestOlderMessages,
        scrollToMessage,
    ]);

    // Auto-scroll only when a new tail message arrives AND either:
    // - user is already near the bottom, or
    // - the new message was sent by the current user.
    // This avoids jumps on reactions/edits and respects the reader's scroll position.
    useEffect(() => {
        const last = Array.isArray(messages) && messages.length > 0 ? messages[messages.length - 1] : null;
        const tailId = last?.id ?? null;
        const prev = prevTailMetaRef.current;

        const tailChanged = tailId != null && tailId !== prev.tailId; // appended a new message

        if (tailChanged) {
            const container = viewportRef.current;
            let shouldScroll = false;

            if (container) {
                const distanceFromBottom = (container.scrollHeight - container.scrollTop - container.clientHeight);
                const nearBottom = distanceFromBottom <= 96; // px threshold
                const isOwnMessage = currentUserId != null && String(last?.senderId) === String(currentUserId);
                shouldScroll = nearBottom || isOwnMessage;
            } else {
                // Fallback: if the container isn't ready, default to scrolling
                shouldScroll = true;
            }

            if (shouldScroll && container) {
                performScrollToBottom({ behavior: "smooth" });
            }
        }

        prevTailMetaRef.current = { tailId, length: Array.isArray(messages) ? messages.length : 0 };
    }, [messages, currentUserId, performScrollToBottom]);

    const activeTypingKey = useMemo(() => {
        if (!Array.isArray(typingUserIds) || typingUserIds.length === 0) {
            return "";
        }
        return typingUserIds
            .filter((userId) => userId !== undefined && userId !== null && String(userId).trim())
            .map((userId) => String(userId).trim())
            .sort()
            .join("|");
    }, [typingUserIds]);

    useEffect(() => {
        const previousTypingKey = prevTypingKeyRef.current;
        prevTypingKeyRef.current = activeTypingKey;

        if (!activeTypingKey || activeTypingKey === previousTypingKey) {
            return;
        }

        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }

        const distanceFromBottom = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
        if (distanceFromBottom <= DEFAULT_TYPING_HEIGHT + 96) {
            performScrollToBottom({ behavior: "smooth" });
        }
    }, [activeTypingKey, performScrollToBottom]);

    const typingIndicator = useMemo(() => {
        if (!typingUserIds || typingUserIds.length === 0) {
            return null;
        }

        return (
            <ChatTypingIndicator
                key="typing-indicator"
                typingUserIds={typingUserIds}
                resolveUserName={resolveUserName}
            />
        );
    }, [typingUserIds, resolveUserName]);

    const renderedMessages = useMemo(() => {
        const nodes = [];
        const rootRef = viewportRef;

        if (hasMoreAbove && typeof onLoadOlderMessages === "function") {
            if (isFetchingOlder) {
                nodes.push(
                    <VirtualizedItem
                        key="loading-older"
                        rootRef={rootRef}
                        estimateHeight={LOADER_HEIGHT}
                        className="chat-messages__virtual-item"
                    >
                        <div className="chat-messages__loader" role="status" aria-live="polite">
                            <LoadingSpinner />
                            <span className="chat-messages__loader-text">Loading messages…</span>
                        </div>
                    </VirtualizedItem>
                );
            } else {
                nodes.push(
                    <VirtualizedItem
                        key="load-more"
                        rootRef={rootRef}
                        estimateHeight={LOADER_HEIGHT}
                        className="chat-messages__virtual-item"
                    >
                        <button
                            type="button"
                            className="chat-messages__load-more"
                            onClick={invokeLoadOlder}
                            disabled={isFetchingOlder}
                        >
                            Load older messages
                        </button>
                    </VirtualizedItem>
                );
            }
        }

        messages.forEach((msg, index, array) => {
            const currentTimestamp = msg.timestamp ? new Date(msg.timestamp) : null;
            const previousTimestamp = index > 0 && array[index - 1]?.timestamp
                ? new Date(array[index - 1].timestamp)
                : null;

            const currentDate = currentTimestamp?.toDateString();
            const prevDate = previousTimestamp?.toDateString();

            if (currentDate && currentDate !== prevDate) {
                const separatorMeta = getDateSeparatorMetadata(msg.timestamp, formatDate);
                const separatorIdentity = separatorMeta.isoDate || currentDate;
                const separatorDiscriminator = msg.id != null && String(msg.id).trim()
                    ? String(msg.id).trim()
                    : index;
                const separatorKey = `date-${separatorIdentity}-${separatorDiscriminator}`;
                nodes.push(
                    <VirtualizedItem
                        key={separatorKey}
                        rootRef={rootRef}
                        estimateHeight={DEFAULT_SEPARATOR_HEIGHT}
                        className="chat-messages__virtual-item"
                        dataAttributes={{
                            "data-date-separator": "true",
                            "data-date-iso": separatorMeta.isoDate || undefined,
                            "data-date-label": separatorMeta.label || separatorMeta.displayLabel || undefined,
                        }}
                    >
                        <DateSeparator
                            date={msg.timestamp}
                            label={separatorMeta.label}
                            isoDate={separatorMeta.isoDate}
                            displayLabel={separatorMeta.displayLabel}
                        />
                    </VirtualizedItem>
                );
            }

            const isUser = currentUserId != null && String(msg.senderId).trim() === String(currentUserId).trim();
            const profile = typeof resolveUserName === "function" ? resolveUserName(msg.senderId) : null;
            const username = isUser ? selfName : ((profile && (profile.name || profile.username)) || "");
            const avatarUrl = isUser ? (selfAvatar || profile?.avatarUrl) : profile?.avatarUrl;

            const handleReactForMessage = (emojiValue, metadata = {}) => {
                const native = typeof emojiValue === "string" ? emojiValue : emojiValue?.native;
                if (!native) {
                    return;
                }
                const reactions = Array.isArray(msg.reactions) ? msg.reactions : [];
                const alreadyReacted = reactions.some((reaction) => reaction && reaction.emoji === native && Boolean(reaction.reacted));
                const nextMeta = alreadyReacted ? { ...metadata, remove: true } : metadata;
                if (!nextMeta.remove) {
                    bumpFavoriteEmoji(native);
                }
                onReact?.(msg, native, nextMeta);
            };

            const isEditing = String(editingMessageId) === String(msg.id);
            const prevMessage = index > 0 ? array[index - 1] : null;
            const sameAuthorAsPrev = prevMessage && String(prevMessage.senderId).trim() === String(msg.senderId).trim();
            const withinFiveMinutes = (() => {
                if (!prevMessage || !prevMessage.timestamp || !msg.timestamp) {
                    return false;
                }
                try {
                    const deltaMs = Math.abs(new Date(msg.timestamp) - new Date(prevMessage.timestamp));
                    return deltaMs <= 5 * 60 * 1000;
                } catch (_) {
                    return false;
                }
            })();
            const useCompact = sameAuthorAsPrev && withinFiveMinutes && !isEditing;

            const threadReplyCount = Number.isFinite(msg.commentCount)
                ? msg.commentCount
                : (Array.isArray(msg.comments) ? msg.comments.length : 0);

            const openComments = () => {
                setCommentState({ open: true, message: msg });
                onComment?.(msg);
                notifyCommentStateChange(true, msg);
            };

            const handleMore = (event) => {
                handleMoreClick(msg, event);
                onMore?.(msg);
            };

            const htmlContent = String(msg?.content || "");
            const supportedPreviewUrls = extractSupportedPreviewUrlsFromHtml(htmlContent);
            const attachments = Array.isArray(msg?.attachments) ? msg.attachments : [];
            const imageAttachments = attachments.filter(
                (attachment) => attachment
                    && String(attachment.type || "IMAGE").toUpperCase() === "IMAGE"
                    && attachment.url
            );
            const inlineImageUrls = extractInlineImageUrlsFromHtml(htmlContent);
            const attachmentItems = imageAttachments.length ? attachmentsToItems(imageAttachments) : [];
            const shouldRenderGallery = imageAttachments.length > 0
                && !imageAttachments.some((attachment) => inlineImageUrls.has(normalizeInlineImageUrl(attachment.url)));

            const deliveryStatus =
                typeof resolveMessageStatus === "function"
                    ? resolveMessageStatus(msg)
                    : null;

            const mergedDeliveryStatus = deliveryStatus || msg.deliveryStatus;
            const messageForBubble = {
                ...msg,
                edited: Boolean(msg.edited),
                deliveryStatus: mergedDeliveryStatus,
            };

            const attachmentGallery = shouldRenderGallery ? (
                <div className="message-row__attachments" role="group" aria-label="Attachments">
                    {imageAttachments.map((attachment, indexInGallery) => {
                        const alt = attachment.contentType
                            ? `${attachment.contentType} attachment`
                            : "Image attachment";
                        const handleClick = (event) => {
                            if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1)) {
                                return;
                            }
                            event?.preventDefault?.();
                            openWithItems(attachmentItems, indexInGallery);
                        };
                        return (
                            <a
                                key={attachment.id || attachment.url}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="message-row__attachment"
                                onClick={handleClick}
                            >
                                <ChatImage
                                    src={attachment.url}
                                    alt={alt}
                                    variant="gallery"
                                />
                            </a>
                        );
                    })}
                </div>
            ) : null;

            const richContent = (
                <div className="message-row__rich" onClick={getRichContentClickHandler(msg.content)}>
                    {contentRenderer ? contentRenderer(msg.content) : <RichTextRenderer html={msg.content} />}
                </div>
            );

            const combinedContent = (
                <ContentWithLinkPreviews previewUrls={supportedPreviewUrls}>
                    {richContent}
                </ContentWithLinkPreviews>
            );

            const replyPreview = getMessageReplyPreview(msg);
            const quotedReply = replyPreview ? (
                <MessageReplyQuote
                    reply={replyPreview}
                    currentUserId={currentUserIdString}
                    currentUser={authUser}
                    resolveUserName={resolveUserName}
                    fallbackAuthor={t("chatPage.anonymousUser")}
                    fallbackPreview={t("chatPage.emptyMessagePlaceholder")}
                    onOpen={handleOpenReplyQuote}
                    ariaLabel={t("chatPage.jumpToRepliedMessage")}
                />
            ) : null;

            const contentWithAttachments = (
                <>
                    {quotedReply}
                    {combinedContent}
                    {attachmentGallery}
                </>
            );

            const renderEditableContent = () => {
                if (!isEditing) {
                    return contentWithAttachments;
                }
                return (
                    <MessageInlineEditor
                        initialHtml={msg.content || ""}
                        onCancel={() => setEditingMessageId(null)}
                        onSave={async (nextHtml) => {
                            const trimmed = String(nextHtml || "").trim();
                            const currentTrim = String(msg.content || "").trim();
                            if (!trimmed || trimmed === currentTrim) {
                                setEditingMessageId(null);
                                return;
                            }
                            try {
                                const updated = await chatService.editMessage(msg.id, trimmed);
                                onEditMessage?.(updated);
                            } catch (error) {
                                // eslint-disable-next-line no-console
                                console.error("Failed to edit message", error);
                            } finally {
                                setEditingMessageId(null);
                            }
                        }}
                        autoFocus
                        ariaLabel="Edit message"
                    />
                );
            };

            const contentNode = useBubbleLayout ? (
                <ChatBubbleRow
                    message={messageForBubble}
                    messageId={msg.id}
                    currentUserId={currentUserId}
                    content={renderEditableContent()}
                    activeMessageId={activeMessageId}
                    setActiveMessageId={setActiveMessageId}
                    quickEmojis={favoriteQuickEmojis}
                    onQuickReact={(emoji) => handleReactForMessage(emoji, { remove: false, source: "quick" })}
                    onEmojiPickerOpenChange={(open) => {
                        if (!open && activeMessageId === msg.id) {
                            setActiveMessageId(null);
                        }
                    }}
                    onMore={handleMore}
                    reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                    onToggleReaction={(emoji, reacted) => handleReactForMessage(emoji, { remove: reacted, source: "chip" })}
                    canReact={canReact}
                    threadReplyCount={threadReplyCount}
                    onOpenThread={openComments}
                    onRightClick={(event) => handleRightClick(event, msg.id)}
                    isEditing={isEditing}
                    formatLocalTime={formatMessageTime}
                />
            ) : useCompact ? (
                <MessageRowCompact
                    messageId={msg.id}
                    activeMessageId={activeMessageId}
                    setActiveMessageId={setActiveMessageId}
                    timestamp={msg.timestamp}
                    status={deliveryStatus}
                    reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                    onReact={handleReactForMessage}
                    onMore={handleMore}
                    quickEmojis={favoriteQuickEmojis}
                    canReact={canReact}
                    threadReplyCount={threadReplyCount}
                    onOpenThread={openComments}
                >
                    {contentWithAttachments}
                </MessageRowCompact>
            ) : (
                <MessageRow
                    messageId={msg.id}
                    activeMessageId={activeMessageId}
                    setActiveMessageId={setActiveMessageId}
                    userId={msg.senderId}
                    username={username}
                    avatarUrl={avatarUrl}
                    timestamp={msg.timestamp}
                    edited={Boolean(msg.edited)}
                    status={deliveryStatus}
                    onReact={handleReactForMessage}
                    onMore={handleMore}
                    quickEmojis={favoriteQuickEmojis}
                    reactions={Array.isArray(msg.reactions) ? msg.reactions : []}
                    canReact={canReact}
                    isEditing={isEditing}
                    threadReplyCount={threadReplyCount}
                    onOpenThread={openComments}
                >
                    {renderEditableContent()}
                </MessageRow>
            );

            nodes.push(
                <VirtualizedItem
                    key={`msg-${msg.id ?? index}`}
                    rootRef={rootRef}
                    estimateHeight={DEFAULT_MESSAGE_HEIGHT}
                    className="chat-messages__virtual-item"
                    dataAttributes={{ "data-message-id": msg.id }}
                >
                    <div
                        className="chat-messages__item"
                        data-message-id={msg.id}
                        onContextMenu={useBubbleLayout ? undefined : ((event) => handleRightClick(event, msg.id))}
                    >
                        {contentNode}
                    </div>
                </VirtualizedItem>
            );
        });

        if (typingIndicator) {
            nodes.push(
                <VirtualizedItem
                    key="typing-indicator"
                    rootRef={rootRef}
                    estimateHeight={DEFAULT_TYPING_HEIGHT}
                    className="chat-messages__virtual-item chat-messages__virtual-item--typing"
                >
                    {typingIndicator}
                </VirtualizedItem>
            );
        }

        return nodes;
    }, [
        messages,
        currentUserId,
        currentUserIdString,
        typingIndicator,
        contentRenderer,
        resolveUserName,
        onReact,
        onComment,
        onMore,
        favoriteQuickEmojis,
        authUser,
        selfName,
        selfAvatar,
        activeMessageId,
        handleMoreClick,
        handleRightClick,
        canReact,
        editingMessageId,
        onEditMessage,
        viewportRef,
        hasMoreAbove,
        isFetchingOlder,
        onLoadOlderMessages,
        invokeLoadOlder,
        handleOpenReplyQuote,
        getRichContentClickHandler,
        openWithItems,
        notifyCommentStateChange,
        resolveMessageStatus,
        useBubbleLayout,
        formatDate,
        formatMessageTime,
        t,
    ]);

    const handleCloseComments = useCallback(() => {
        if (!commentState.open) {
            return;
        }
        setCommentState({ open: false, message: null });
        notifyCommentStateChange(false, null);
    }, [commentState.open, notifyCommentStateChange]);

    const contextMenuItems = useMemo(() => {
        if (!menuState.open) {
            return [];
        }
        const target = messages.find((m) => m && String(m.id) === String(menuState.messageId));
        return buildContextMenuItems(target);
    }, [menuState.open, menuState.messageId, messages, buildContextMenuItems]);

    useEffect(() => {
        if (!deleteMessageTarget?.id || !Array.isArray(messages)) {
            return;
        }
        const messageStillExists = messages.some((message) => (
            message && String(message.id) === String(deleteMessageTarget.id)
        ));
        if (!messageStillExists) {
            setDeleteMessageTarget(null);
        }
    }, [deleteMessageTarget, messages]);

    useEffect(() => {
        if (typeof commentPanelState === "undefined") {
            if (!commentState.open) {
                return;
            }
            if (!commentMessageId) {
                setCommentState({ open: false, message: null });
                return;
            }
            const messageStillExists = Array.isArray(messages)
                ? messages.some((message) => message && String(message.id) === commentMessageId)
                : false;
            if (!messageStillExists) {
                setCommentState({ open: false, message: null });
            }
            return;
        }

        if (!commentPanelState || !commentPanelState.open || !commentPanelState.messageId) {
            if (commentState.open) {
                setCommentState({ open: false, message: null });
            }
            return;
        }

        const targetId = String(commentPanelState.messageId);
        const targetMessage = Array.isArray(messages)
            ? messages.find((message) => message && String(message.id) === targetId)
            : null;

        if (targetMessage) {
            if (!commentState.open || commentMessageId !== targetId || commentState.message !== targetMessage) {
                setCommentState({ open: true, message: targetMessage });
            }
        } else if (commentState.open) {
            setCommentState({ open: false, message: null });
            notifyCommentStateChange(false, null);
        }
    }, [
        commentPanelState,
        messages,
        commentState.open,
        commentState.message,
        commentMessageId,
        notifyCommentStateChange,
    ]);

    // Persist comments pane width
    useEffect(() => {
        try { window.localStorage.setItem("chat.commentsWidth", String(commentsWidth)); } catch (_) {}
    }, [commentsWidth]);

    const clampCommentsWidth = useCallback((value) => {
        const min = 320;
        const max = 720;
        return Math.max(min, Math.min(value, max));
    }, []);

    const handleResizeMove = useCallback((event) => {
        const root = containerRef.current;
        if (!root) return;
        const rect = root.getBoundingClientRect();
        const proposed = rect.right - event.clientX;
        setCommentsWidth(clampCommentsWidth(proposed));
    }, [clampCommentsWidth]);

    const stopResize = useCallback(() => {
        setIsResizing(false);
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', stopResize);
    }, [handleResizeMove]);

    const startResize = useCallback((event) => {
        event.preventDefault();
        setIsResizing(true);
        window.addEventListener('mousemove', handleResizeMove);
        window.addEventListener('mouseup', stopResize);
    }, [handleResizeMove, stopResize]);

    const handleResizerKeyDown = useCallback((event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setCommentsWidth((w) => clampCommentsWidth(w + 16));
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            setCommentsWidth((w) => clampCommentsWidth(w - 16));
        } else if (event.key === 'Home') {
            event.preventDefault();
            setCommentsWidth(380);
        }
    }, [clampCommentsWidth]);

    const stickyDateRecalculateKey = useMemo(() => ({
        messages,
        formatDate,
        hasMoreAbove,
        isFetchingOlder,
        isInitialLoading,
    }), [messages, formatDate, hasMoreAbove, isFetchingOlder, isInitialLoading]);

    const handleDeleteMessageForEveryone = useCallback(async (message) => {
        if (!message?.id) {
            return;
        }
        await chatService.deleteMessage(message.id);
        onDeleteMessage?.(message.id);
    }, [onDeleteMessage]);

    const canDeleteTargetForEveryone = Boolean(
        deleteMessageTarget
        && currentUserIdString
        && String(deleteMessageTarget.senderId) === currentUserIdString
    );

    // Inline comment send/IME handling moved into MessageCommentDrawer

    return (
        <>
        <div className="chat-messages" role="log" aria-live="polite" ref={containerRef}>
            <div className="chat-messages__backdrop" aria-hidden="true" />
            <div className="chat-messages__viewport-shell">
                <ChatStickyOverlay className="chat-messages__sticky-overlay" topOffsetPx={STICKY_DATE_TOP_PX}>
                    <StickyDateHeader
                        className="chat-messages__sticky-date"
                        scrollRef={viewportRef}
                        stickyTopPx={STICKY_DATE_TOP_PX}
                        recalculateKey={stickyDateRecalculateKey}
                    />
                </ChatStickyOverlay>
                <div className="chat-messages__scroll-layer">
                    <ScrollArea
                        ref={viewportRef}
                        className="chat-messages__viewport"
                        ariaLabel="Conversation messages"
                    >
                        {isInitialLoading && (!Array.isArray(messages) || messages.length === 0) ? (
                            <div className="chat-messages__initial-loader" role="status" aria-live="polite">
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                {renderedMessages}
                                <div className="chat-messages__bottom-anchor" ref={bottomAnchorRef} aria-hidden="true" />
                            </>
                        )}
                    </ScrollArea>
                </div>
            </div>
            {commentState.open && (
                <>
                    <div
                        className={`chat-messages__resizer${isResizing ? " chat-messages__resizer--dragging" : ""}`}
                        role="separator"
                        aria-orientation="vertical"
                        aria-controls="comments-pane"
                        tabIndex={0}
                        onMouseDown={startResize}
                        onKeyDown={handleResizerKeyDown}
                        title={t("chatPage.comments.resizePane", "Resize comments pane")}
                    />
                    <aside
                        id="comments-pane"
                        className="chat-messages__comments"
                        style={{ width: `${commentsWidth}px` }}
                        aria-label={t("chatPage.comments.dialogLabel", "Message comments")}
                    >
                        <header className="chat-messages__comments-header">
                            <h2 className="chat-messages__comments-title">{t("chatPage.comments.title", "Comments")}</h2>
                            <button type="button" className="chat-messages__comments-close" aria-label={t("chatPage.comments.close", "Close comments")} onClick={handleCloseComments}>×</button>
                        </header>
                        <div className="chat-messages__comments-body">
                            <MessageCommentDrawer
                                isOpen
                                onClose={handleCloseComments}
                                message={commentState.message}
                                messageContentRenderer={contentRenderer}
                                onSubmit={(evt) => {
                                  if (!evt || !commentState.message?.id || typeof setMessages !== 'function') return;
                                  const mid = String(commentState.message.id);
                                  if (evt.type === 'add') {
                                    setMessages((prev) => prev.map((m) => {
                                      if (String(m.id) !== mid) return m;
                                      const prevCount = Number.isFinite(m.commentCount) ? m.commentCount : (Array.isArray(m.comments) ? m.comments.length : 0);
                                      const nextCount = Number.isFinite(prevCount) ? prevCount + 1 : 1;
                                      return { ...m, commentCount: nextCount };
                                    }));
                                  } else if (evt.type === 'delete') {
                                    setMessages((prev) => prev.map((m) => {
                                      if (String(m.id) !== mid) return m;
                                      const prevCount = Number.isFinite(m.commentCount) ? m.commentCount : (Array.isArray(m.comments) ? m.comments.length : 0);
                                      const nextCount = Math.max(0, (Number.isFinite(prevCount) ? prevCount - 1 : 0));
                                      return { ...m, commentCount: nextCount };
                                    }));
                                  }
                                }}
                                stompClient={stompClient}
                                isSocketConnected={isSocketConnected}
                                resolveUserName={resolveUserName}
                                variant="inline"
                                showHeader={false}
                            />
                        </div>
                    </aside>
                </>
            )}
            {menuState.open && contextMenuItems.length > 0 && (
                <DropdownMenuFloat
                    position={{ x: menuState.x, y: menuState.y }}
                    onClose={closeMenu}
                    items={contextMenuItems}
                />
            )}
        </div>
        {deleteMessageTarget && typeof document !== "undefined" && ReactDOM.createPortal(
            <PopupOverlay
                onClose={closeDeleteMessagePopup}
                closeOnOverlayClick
                closeOnEsc
                ariaLabel={t("chat.deleteMessage.dialogLabel")}
            >
                <DeleteMessagePopup
                    message={deleteMessageTarget}
                    allowDeleteForEveryone={canDeleteTargetForEveryone}
                    onDeleteForEveryone={handleDeleteMessageForEveryone}
                    onDeleteForMe={onDeleteForMe}
                    closeModal={closeDeleteMessagePopup}
                />
            </PopupOverlay>,
            document.getElementById("portal") || document.body
        )}
        {lightboxEl}
        </>
    );
});

export default ChatMessages;
